import * as React from 'react';
import { FastField, FastFieldProps } from 'formik';

const { CheckBox } = require('@cvent/carina-checkbox');
import { Search } from '@weddingspot/ws-api-client';

import { ContentCollapsible, ContentPersistent } from '../input-group-panel';
import { InputGroupPanel } from './InputGroupPanel';
import { SRPFormikValues } from '.';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';

const _styles = require('./LocationsFilters.scss');

interface LocationsFiltersChoices {
    locations: Search.LocationsFilterChoices;
    collapsibleSubsections: boolean;
}

const NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT = 3;

/**
 * We wrap the checkbox in the formik FastField element to prevent re-renders unless
 * values[fieldName] changes
 */
const RegionCheckbox = (props: { market: string; region: Search.BasicFilterChoice }) => {
    const { market, region } = props;

    const locationKey = `${market}:${region.label}`;
    const fieldName = `region.${locationKey}`;

    return (
        <FastField name={fieldName}>
            {(props: FastFieldProps<SRPFormikValues>) => {
                const { field } = props;
                // We need to omit the value prop from filed before passing this down from
                // checkbox since semantically they aren't the same thing (i.e. HTML checkbox value attribute
                // shouldn't change like the formik field value)
                const { value, onChange, ..._field } = field;

                return (
                    <div className={_styles.checkboxWrapper}>
                        <CheckBox
                            {..._field}
                            id={field.name}
                            checked={value}
                            onChange={(e: React.ChangeEvent) => {
                                setTimeout(() => {
                                    props.form.setFieldValue('bbox', null);
                                });
                                onChange(e);
                                logEvent(AnalyticsEvent.EVTNM_CLICKED_FILTER, {
                                    Type: 'Region',
                                    'Current Market': market,
                                    Label: region.label,
                                    Value: region.label,
                                    Checked: !value,
                                    'SRP Version': 2,
                                });
                            }}
                        >
                            {region.label}
                        </CheckBox>
                    </div>
                );
            }}
        </FastField>
    );
};

const MarketCheckbox = (props: { market: string; submarket: Search.BasicFilterChoice }) => {
    const { market, submarket } = props;

    const locationKey = `${market}:${submarket.label}`;
    const fieldName = `market.${locationKey}`;

    return (
        <FastField name={fieldName}>
            {(props: FastFieldProps<SRPFormikValues>) => {
                const { field } = props;
                const { value, onChange, ..._field } = field;

                return (
                    <div className={_styles.checkboxWrapper}>
                        <CheckBox
                            {..._field}
                            id={field.name}
                            checked={value}
                            onChange={(e: React.ChangeEvent) => {
                                onChange(e);
                            }}
                        >
                            {submarket.label}
                        </CheckBox>
                    </div>
                );
            }}
        </FastField>
    );
};

export const LocationsFilters = (props: LocationsFiltersChoices) => {
    const { locations, collapsibleSubsections } = props;

    return (
        <>
            {locations.options.map((market: Search.LocationsFilterChoice, idx) => {
                const collapsible = market.regions.length > NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT;
                const sliceIdx = collapsible ? NUM_CHECKBOX_ROWS_VISIBLE_DEFAULT : market.regions.length;

                const renderRegionCheckbox = (region: Search.BasicFilterChoice, idx: number) => (
                    <RegionCheckbox key={idx} market={market.label} region={region} />
                );
                const renderMarketCheckbox = (submarket: Search.BasicFilterChoice, idx: number) => (
                    <MarketCheckbox key={idx} market={market.label} submarket={submarket} />
                );

                return (
                    <InputGroupPanel
                        key={idx}
                        title={market.label}
                        collapsible={collapsibleSubsections && collapsible}
                        defaultCollapsed={collapsible && idx > 0}
                    >
                        <ContentPersistent>{market.regions.slice(0, sliceIdx).map(renderRegionCheckbox)}</ContentPersistent>
                        <ContentCollapsible>{() => <>{market.regions.slice(sliceIdx).map(renderRegionCheckbox)}</>}</ContentCollapsible>
                        <ContentPersistent>{market.markets.slice(0, sliceIdx).map(renderMarketCheckbox)}</ContentPersistent>
                        <ContentCollapsible>{() => <>{market.markets.slice(sliceIdx).map(renderMarketCheckbox)}</>}</ContentCollapsible>
                    </InputGroupPanel>
                );
            })}
        </>
    );
};
